<template>
  <b-overlay :show="plotLoading" rounded="sm" class="h-100">
    <template v-slot:overlay>
      <div class="text-center">
        <b-icon icon="stopwatch" font-scale="3" animation="cylon"></b-icon>
        <br />
        <div id="datastatus-label" v-if="dataStatus !== null">
          <p>{{ dataStatus }}</p>
          <p v-if="timeElapsed">Time elapsed: {{ timeElapsed }}</p>
          <b-btn
            size="sm"
            variant="outline-danger"
            @click="cancelRequest"
          ><i class="fas fa-exclamation-triangle fa-sm"></i>
          CANCEL</b-btn>
        </div>
      </div>
    </template>
    <b-container fluid class="h-100">
      <b-row>
        <b-col>
          <b-alert v-model="showDismissibleAlert" variant="danger" dismissible>
            Data service unavailable. Request failed. Please try again later ...
          </b-alert>
          <b-alert
            :show="dismissCountDown"
            variant="danger"
            dismissible
            @dismiss-count-down="countDownChanged"
            fade
          >
            Plotting parameters from different infrastructures
            is not supported at this time.
          </b-alert>
        </b-col>
      </b-row>
      <b-row style="min-height: 50px;">
        <b-col cols="1" class="text-center p-2" style="max-width: 5%;">
          <!--Popover on instrument bar to describe interpolation and data availability to users -->
          <b-popover
            target="popover-reactive-1"
            triggers="hover"
            boundary="window"
          >
            <template #title>This is the instrument bar.</template>
            {{instBarHoverContent}}
          </b-popover>
          <i
            class="fas fa-info-circle"
            id="popover-reactive-1"
          ></i>
        </b-col>
        <b-col cols="6">
          <instrument-bar :trace="currentTrace"></instrument-bar>
        </b-col>
        <b-col cols="5" class="text-right">
          <!--
            Disabled state for 'Update Plot' btn checks for:
              (1) selectedInstrument length (needs to be over 1),
              (2) whether the request is a valid request,
              (3) whether startDate is disabled,
              (4) whether endDate is disabled,
              (5) whether the dateTime is in an invalid state,
            Check #3, 4, and 5 correspond to the alert states that popup when
            date data is empty or invalid.
          -->
          <b-button
            class="mt-2"
            size="sm"
            variant="primary"
            @click="requestData"
            :disabled="selectedInstruments.length < 1 || !validRequest
            || startDateDisabled || endDateDisabled
            || !startTimeValid || !endTimeValid || !startDateValid || !endDateValid"
            >Update Plot
          </b-button>
          <!--Reset btn enables when selectedInstruments is more than 1-->
          <b-button
            size="sm"
            class="mt-2"
            variant="danger"
            @click="resetForm()"
            :disabled="!selectedInstruments.length > 0"
            >Reset Form
          </b-button>
        </b-col>
      </b-row>
      <b-row class="pt-1" style="max-height: 80%;overflow-y: auto;">
    <!-- Parameter selection -->
        <b-col cols="6">
          <b-row>
            <b-col>
              <p style="font-weight: bold">Select some parameters...</p>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-input-group class="mb-3">
                <template #prepend>
                  <b-input-group-text>X-axis
                    (<i
                      class="fas fa-long-arrow-alt-right"
                      style="margin-left: 1px; margin-right: 1px"
                    ></i>)
                  </b-input-group-text>
                </template>
                <b-form-select
                  id="x-axis-select"
                  v-model="xAxis"
                  :options="parameterList"
                  :disabled="emptyParams"
                  v-b-tooltip.hover.right :title="getParamText(xAxis)"
                >
                  <template v-slot:first>
                    <b-form-select-option :value="null" disabled>
                      Parameter 1
                    </b-form-select-option>
                  </template>
                </b-form-select>
              </b-input-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-input-group class="mb-2">
                <template #prepend>
                  <b-input-group-text>Y-axis
                    (<i
                      class="fas fa-long-arrow-alt-up"
                      style="margin-left: 5px; margin-right: 5px"
                    ></i>)
                  </b-input-group-text>
                </template>
                <b-form-select
                  id="y-axis-select"
                  v-model="yAxis"
                  :options="parameterList"
                  :disabled="emptyParams"
                  v-b-tooltip.hover.right :title="getParamText(yAxis)"
                >
                  <template v-slot:first>
                    <b-form-select-option :value="null" disabled>
                      Parameter 2
                    </b-form-select-option>
                  </template>
                </b-form-select>
              </b-input-group>
            </b-col>
          </b-row>
          <!-- TODO: Z-axis currently not available. Need to be integrate in
          future -->
          <!-- <b-row>
            <b-col>
              <b-form-select
                id="y-axis-select"
                v-model="selectedAxis.z"
                :options="parameterList"
                :disabled="emptyParams"
                v-b-tooltip.hover.right :title="getParamText(selectedAxis.z)"

              >
                <template v-slot:first>
                  <b-form-select-option
                    :value="null"
                    disabled>
                    -- Select a Parameter --
                  </b-form-select-option>
                </template>
              </b-form-select>
              Parameter 3 (z-axis)
            </b-col>
          </b-row> -->
          <b-row>
            <b-col>
              <b-form-checkbox
                v-model="colorActive"
                :disabled="emptyParams"
                name="check-button"
                class="pt-1 mb-1"
              >
              <strong>{{colorBarText}}</strong>
              </b-form-checkbox>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-container v-if="colorActive">
                <b-row>
                  <b-col>
                    <!--The alert will show up if color parameter is not filled in-->
                    <b-alert
                      variant="danger"
                      :show="colorAxisInvalidState"
                    >
                      Enter a color bar value.
                    </b-alert>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col>
                    <b-input-group class="mb-3">
                      <template #prepend>
                        <b-input-group-text>Colorbar</b-input-group-text>
                      </template>
                      <b-form-select
                        id="color-axis-select"
                        v-model="colorValue"
                        :options="parameterList"
                        :disabled="emptyParams"
                        v-b-tooltip.hover.right :title="getParamText(colorValue)"
                      >
                        <template v-slot:first>
                          <b-form-select-option :value="null" disabled>
                            Parameter 3
                          </b-form-select-option>
                        </template>
                      </b-form-select>
                    </b-input-group>
                    <b-input-group class="mb-3">
                      <template #prepend>
                        <b-input-group-text>Range</b-input-group-text>
                      </template>
                      <b-form-input
                        type="number"
                        v-model="colorMin"
                        :disabled="emptyParams"
                        placeholder="Min"
                        v-b-tooltip.hover.right :title="getColorMin(colorMin)"
                      ></b-form-input>
                      <b-form-input
                        type="number"
                        v-model="colorMax"
                        :disabled="emptyParams"
                        placeholder="Max"
                        v-b-tooltip.hover.right :title="getColorMax(colorMax)"
                      ></b-form-input>
                    </b-input-group>

                    <b-input-group class="mb-3">
                      <template #prepend>
                        <b-input-group-text>Theme&nbsp;
                          <i
                            class="fas fa-info-circle"
                            id="cmocean-details"
                            style="margin-left: 2px;"
                          ></i>
                          <b-popover target="cmocean-details" triggers="hover">
                            <template #title>
                              <strong>Beautiful Colormaps for oceanography: cmocean</strong>
                            </template>
                            <p>
                              These colormaps were chosen to be perceptually uniform
                              and to reflect the data they are representing in
                              terms of being sequential, divergent,
                              or cyclic (phase colormap), and to be intuitive.
                            </p>
                            <p>
                              <strong>True Colors of Oceanography</strong> <br>
                              <cite>
                              Thyng, K. M., Greene, C. A., Hetland, R. D.,
                              Zimmerle, H. M., & DiMarco, S. F. (2016).
                              True colors of oceanography. Oceanography, 29(3), 10.
                              </cite>
                              <br>
                              DOI: 10.5670/oceanog.2016.66
                            </p>
                          </b-popover>
                        </b-input-group-text>
                      </template>
                      <b-form-select
                        id="colorbar-select"
                        v-model="colorTheme"
                        :options="colorbarChoices"
                        :disabled="emptyParams"
                        :title="colorTheme"
                        v-b-tooltip.hover.right
                      >
                        <!-- This slot appears above the options from 'options' prop -->
                        <template v-slot:first>
                          <b-form-select-option :value="null" disabled>
                            cmocean
                          </b-form-select-option>
                        </template>
                      </b-form-select>
                    </b-input-group>
                  </b-col>
                </b-row>
                <!-- Separates the color bar barClass on new row
                so the input is not squished on screens -->
                <b-row>
                  <b-col>
                    <b-card id="colorbar-barclass" :class="barClass"></b-card>
                  </b-col>
                </b-row>
              </b-container>
            </b-col>
          </b-row>
        </b-col>
        <!-- Date/Time Selection -->
        <b-col cols="6">
          <b-row>
            <b-col>
              <p style="font-weight: bold">Choose the date/time range
                <i
                  id="datetime-popover"
                  class="fas fa-info-circle"
                  style="margin-left: 2px;"
                  title="How to select a Date Time range"
                ></i>
                <b-popover
                  target="datetime-popover"
                  triggers="hover"
                  boundary="window"
                >
                  <template #title>Pre-select a Date Time range</template>
                  {{dateTimeNote}}
                </b-popover>
              </p>
              <!--Alert that will popup if dates are invalid-->
              <b-alert
                variant="danger"
                :show="dateTimeInvalidState"
              >
                Enter a valid start/end date and time. Click calendar icon to see available dates.
              </b-alert>
              <b-alert
                variant="danger"
                :show="startDateDisabled || endDateDisabled"
              >
                The selected
                <span v-if="startDateDisabled"> start </span>
                <span v-if="startDateDisabled && endDateDisabled"> and </span>
                <span v-if="endDateDisabled"> end </span>
                date does not contain data. Click calendar icon to see available dates.
              </b-alert>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-row>
                <b-col>
                  <b-button-group>
                    <b-button
                      size="sm"
                      variant="outline-primary"
                      class="ml-auto"
                      @click="setOneDay"
                      :disabled="noParamsSelected"
                    >
                      One Day
                    </b-button>
                    <b-button
                      size="sm"
                      variant="outline-primary"
                      class="ml-auto"
                      @click="setOneWeek"
                      :disabled="noParamsSelected"
                    >
                      One Week
                    </b-button>
                    <b-button
                      size="sm"
                      variant="outline-primary"
                      class="ml-auto"
                      @click="setOneMonth"
                      :disabled="noParamsSelected"
                    >
                      One Month
                    </b-button>
                    <b-button
                      size="sm"
                      variant="outline-primary"
                      class="ml-auto"
                      @click="setSixMonths"
                      :disabled="noParamsSelected"
                    >
                      Six Months
                    </b-button>
                    <b-button
                      size="sm"
                      variant="outline-primary"
                      class="ml-auto"
                      @click="setOneYear"
                      :disabled="noParamsSelected"
                    >
                      One year
                    </b-button>
                  </b-button-group>
                  <hr>
                  <b-input-group class="mb-3">
                    <template #prepend>
                      <b-input-group-text>Start Date</b-input-group-text>
                    </template>
                    <b-form-input
                      id="start-date-input"
                      v-model="startDate"
                      type="text"
                      placeholder="YYYY-MM-DD"
                      autocomplete="off"
                      :state="startDateValid"
                      :disabled="noParamsSelected"
                      v-b-tooltip.hover.right :title="startDate"
                    ></b-form-input>
                    <b-input-group-append>
                      <b-form-datepicker
                        v-model="startDate"
                        button-only
                        right
                        dropdown
                        boundary="window"
                        locale="en-US"
                        aria-controls="start-date-input"
                        :disabled="noParamsSelected"
                        :date-disabled-fn="dateDisabled"
                        :date-info-fn="dateClass"
                        :min="dtMin" :max="dtMax"
                      ></b-form-datepicker>
                    </b-input-group-append>
                  </b-input-group>
                  <b-input-group class="mb-3">
                    <template #prepend>
                      <b-input-group-text>Start Time (UTC)</b-input-group-text>
                    </template>
                    <b-form-input
                      id="start-time-input"
                      v-model="startTime"
                      type="text"
                      placeholder="HH:mm:ss"
                      :state="startTimeValid"
                      :disabled="noParamsSelected"
                      v-b-tooltip.hover.right :title="startTime"
                    ></b-form-input>
                    <b-input-group-append>
                      <b-form-timepicker
                        v-model="startTime"
                        button-only
                        right
                        dropdown
                        boundary="window"
                        show-seconds
                        locale="en"
                        aria-controls="start-time-input"
                        :disabled="noParamsSelected"
                      ></b-form-timepicker>
                    </b-input-group-append>
                  </b-input-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <b-input-group class="mb-3">
                    <template #prepend>
                      <b-input-group-text>End Date</b-input-group-text>
                    </template>
                    <b-form-input
                      id="end-date-input"
                      v-model="endDate"
                      type="text"
                      placeholder="YYYY-MM-DD"
                      autocomplete="off"
                      :state="endDateValid"
                      :disabled="noParamsSelected"
                      v-b-tooltip.hover.right :title="endDate"
                    ></b-form-input>
                    <b-input-group-append>
                      <b-form-datepicker
                        v-model="endDate"
                        button-only
                        right
                        dropdown
                        boundary="window"
                        locale="en-US"
                        aria-controls="end-date-input"
                        :disabled="noParamsSelected"
                        :date-disabled-fn="dateDisabled"
                        :date-info-fn="dateClass"
                        :min="dtMin" :max="dtMax"
                      ></b-form-datepicker>
                    </b-input-group-append>
                  </b-input-group>
                  <b-input-group>
                    <template #prepend>
                      <b-input-group-text>End Time (UTC)</b-input-group-text>
                    </template>
                    <b-form-input
                      id="end-time-input"
                      v-model="endTime"
                      type="text"
                      placeholder="HH:mm:ss"
                      :disabled="noParamsSelected"
                      :state="endTimeValid"
                      v-b-tooltip.hover.right :title="endTime"
                    ></b-form-input>
                    <b-input-group-append>
                      <b-form-timepicker
                        v-model="endTime"
                        button-only
                        right
                        dropdown
                        boundary="window"
                        show-seconds
                        locale="en"
                        aria-controls="end-time-input"
                        :disabled="noParamsSelected"
                      ></b-form-timepicker>
                    </b-input-group-append>
                  </b-input-group>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-container>
  </b-overlay>
</template>

<script>
/* eslint radix: ["error", "as-needed"] */

import _ from 'lodash';
import axios from 'axios';
import { DateTime, Duration } from 'luxon';
import { mapState, mapActions, mapGetters } from 'vuex';
import { decode } from '@msgpack/msgpack';
import colorscales from '@/components/CMOCEAN.json';
import {
  APIBASEURL, APIMETADATA, APIDA, APIDATA,
} from '@/utils/constants';
import InstrumentBar from './InstrumentBar';

export default {
  props: ['chartUUID'],
  components: {
    'instrument-bar': InstrumentBar,
  },
  data() {
    return {
      // Sites
      // selectedSite: null,
      // Infrastructure
      infraList: [],
      // selectedInfrastructure: null,
      parameterAlert: false,
      // Parameters
      allParameters: {},
      parameterList: [],
      // Datetime input valid states
      // startDateValid: null,
      // startTimeValid: null,
      // endDateValid: null,
      // endTimeValid: null,
      localSelectedInst: [],
      // Controls date time ranges
      dateTimeInvalidState: false,
      invalidTimeState: false,
      invalidDateState: false,
      colorAxisInvalidState: false,
      // Data availability for all instruments
      daAllAvailability: {},
      dataAvailabilityCounts: {},
      // Instruments
      instrumentsList: [],
      queryString: null,
      requestReady: false,
      removeData: true,
      // Loading screen
      ranges_rd: null,
      rangesObj: [],
      dataPointCount: null,
      cminmax: [],
      // Alerts
      showDismissibleAlert: false,
      // Colorbar option
      colorBarText: 'Add a colorbar here...',
      // Save cancelled state
      cancelled: false,
      uniqueInfraCount: 0,
      dismissSecs: 5,
      dismissCountDown: 0,
      instBarHoverContent:
        'When you select an instrument to plot, its icon will appear here. If more than one instrument is chosen, their datasets will be interpolated and the start and end points will be restricted to the dataset with the smallest available time range.',
      interpolationNote:
        'There is a known issue where interpolation will cause a fixed value to be displayed on the color bar if a third parameter is selected that does not have available data for the full time range of the other two parameters.',
      dateTimeNote:
        'Each button pre-selects a start and end date to create a time range including the most recent 24 hours of available data, the most recent week of data, the last full month of data, and so on. These time ranges are determined based on available data of the selected instrument(s).',
      plotReady: false,
      // Data request
      requestBody: null,
      validRequest: false,
      dateTimeRegex: /(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})Z?/,
      dtMin: '',
      dtMax: '',
      lastSelected: null,
    };
  },
  created() {
    /** Set trace selectedAxis in store */
    const paramValues = {
      x: this.selectedParameters.x,
      y: this.selectedParameters.y,
      color: {
        value: this.selectedParameters.color,
        active: this.selectedParameters.color_active,
        range: {
          min: this.selectedParameters.color_min,
          max: this.selectedParameters.color_max,
        },
        colorbar: this.selectedParameters.color_theme,
      },
    };
    _.forIn(paramValues, (value, axis) => this.updateSelected({ id: this.id, axis, value }));

    /** Set dateTimeRange in store */
    this.updateStartDate({
      id: this.id,
      value: this.startDate,
    });
    this.updateStartTime({
      id: this.id,
      value: this.startTime,
    });
    this.updateEndDate({
      id: this.id,
      value: this.endDate,
    });
    this.updateEndTime({
      id: this.id,
      value: this.endTime,
    });

    /**
     * Go through instruments from parameters,
     * and add them to trace selected instruments
     */
    this.paramInstruments.forEach((ref) => {
      const foundInst = _.find(this.traceInstruments, {
        id: this.id,
        ref,
      });
      if (!foundInst) {
        this.addInstrument({
          id: this.id,
          ref,
        });
      }
    });
  },
  async mounted() {
    /** Get parameters list based on selected instruments */
    this.allParameters = await this.getAllParameters(this.selectedInstruments);

    /** Setup for the calendar selection of datetime */
    this.daAllAvailability = await this.getDA(this.selectedInstruments);
    this.setDA(this.selectedAxis);

    if (
      this.chart.hasData === false
      && (
        this.selectedInstruments.length > 1
        || this.validRequest
      )
    ) {
      await this.requestData();
    }
  },
  methods: {
    ...mapActions('visualization', ['renderPlot']),
    ...mapActions('visualization/charts', [
      'updateChartQuery', 'setHasDataFlag',
      'updateChartStatus', 'updatePlotLayout',
      'updatePlotOptions', 'saveLastRequestParams',
      'saveLastJobUUID', 'resetPlotLayout',
    ]),
    ...mapActions('visualization/traces', [
      'addInstrument',
      'removeTrace',
      'updateTrace',
      'resetTrace',
      'updateSelected',
      'updateX',
      'updateY',
      'updateColor',
      'updateStartDate',
      'updateStartTime',
      'updateEndDate',
      'updateEndTime',
      'updateAttribute',
      'removeInstrument',
    ]),
    /**
     * Push route to url based on selection
     * @param {string} selection - Selection name
     * @param {string} value - Selection value
     */
    routeSelections(selection, value = null) {
      const query = {
        ...this.$route.query,
        [selection]: value,
      };
      this.$router.push({ query }).catch((err) => err);
      this.updateChartQuery({ uuid: this.uuid, query });
    },
    /**
     * Validate request body for data endpoint
     * @param {object} request - Request body
     */
    validateRequest(request) {
      /** Check on time */
      if (_.has(request, 'start_dt') && _.has(request, 'end_dt')) {
        if (request.start_dt && request.end_dt) {
          const regex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z?/g;
          const startDT = request.start_dt.match(regex);
          const endDT = request.end_dt.match(regex);

          if (startDT === null) {
            return false;
          }

          if (endDT === null) {
            return false;
          }
        }
      }

      /** Check keys in request body */
      const expectedKeys = [
        'ref', 'x', 'y', 'start_dt',
        'end_dt', 'z', 'color',
        'download_format', 'download',
      ];
      const keyExist = expectedKeys.map((k) => _.has(request, k));
      if (_.findIndex(keyExist, false) !== -1) {
        return false;
      }

      /** Check on refs */
      const refs = _.remove(request.ref.split(','), (r) => r !== '');
      if (refs.length === 0) {
        return false;
      }

      return true;
    },
    /**
     * Method to create proper datetime string
     * @param {*} dateStr - Date string
     * @param {*} timeStr - Time string
     */
    createDTString(dateStr, timeStr) {
      return `${dateStr}T${timeStr}${this.timeZone}`;
    },
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown;
    },
    cancelRequest() {
      clearInterval(this.interval);
      this.plotLoading = false;

      // Push cancel to the backend as well.
      const jobUUID = this.chart.lastJobUUID;
      axios
        .post(`${APIBASEURL}/${APIDATA}/job/${jobUUID}/cancel`, {})
        .then((response) => {
          // eslint-disable-next-line no-console
          console.log(response.data);
          this.cancelled = true;
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.log(error);
        });
    },
    plotLoadingFalse() {
      this.plotLoading = false;
    },
    // fetchRanges(rangesRd, paramVal) {
    //   const that = this;
    //   // Gets the obj with same instrument ref des
    //   const refGroup = _.filter(this.globalRanges, [
    //     'reference_designator',
    //     rangesRd,
    //   ]);
    //   // Gets the specific obj with same inst ref des and parameter value
    //   that.rangesObj = _.filter(refGroup, ['parameter_id_r', paramVal]);
    //   const rangesMin = that.rangesObj[0].global_range_min;
    //   const rangesMax = that.rangesObj[0].global_range_max;
    //   return { rangesMin, rangesMax };
    // },
    setInstrumentDA(instrumentRef, data) {
      this.instrumentDA[instrumentRef] = data[instrumentRef];
    },
    requestDA(instrumentRef) {
      // APIDA, APIMETADATA
      const request = {
        ref: instrumentRef,
      };
      return axios
        .get(`${APIBASEURL}/${APIMETADATA}/${APIDA}`, {
          params: request,
        })
        .then((resp) => {
          this.setInstrumentDA(instrumentRef, resp.data);
        });
    },
    /**
     * Async post method to start data request
     * @param {object} data - Request body for /data post
     */
    async postDataRequest(data) {
      return axios.post(`${APIBASEURL}/${APIDATA}/`, data);
    },
    /**
     * Async get method to get data request job result
     * @param {string} uuid - The Job UUID of request
     */
    async getDataRequest(uuid) {
      const getURL = `${APIBASEURL}/${APIDATA}/job/${uuid}`;
      return axios.get(getURL, {
        params: {
          version: '2.1',
        },
        responseType: 'arraybuffer',
        transformResponse: [(data) => decode(data)],
      });
    },
    /**
     * Start data request based on selected parameters and dates
     * Requesting data uses post call - jobUUID used in get call
     * @param {object} event - Update plot click event
     */
    async requestData() {
      /** extractForm() function creates and saves the request into requestBody
       * and also checks for unique infrastructure counts */
      this.uniqueInfraCount = this.extractForm();
      if (this.uniqueInfraCount !== 1) {
        this.dismissCountDown = this.dismissSecs;
      } else {
        this.plotLoading = true;
        // Clear hasData flag first ...
        this.setHasDataFlag({ uuid: this.uuid, hasData: false });
        const data = this.requestBody;
        const startTime = DateTime.utc();
        this.timeElapsed = null;
        this.plotReady = false;
        // Did a check and this message displays even if only color theme or range is updated
        if (_.isEqual(this.chart.lastRequestParams, data) && !this.cancelled) {
          this.dataStatus = 'Same parameters found. Retrieving data ...';
          // Passes through jobUUID from post response
          await this.performGetData(this.chart.lastJobUUID, startTime);
        } else {
          this.dataStatus = 'Data request initiated. Please wait ...';
          try {
            this.holdData = await this.postDataRequest(data);
            const jobUUID = this.holdData.data.job_uuid;
            // Passes through jobUUID from post response to a function that performs
            // the axios get call. This function has an interval to check the status
            // every 5 seconds
            this.getDataInterval(jobUUID, startTime);
            this.saveLastRequestParams({ uuid: this.uuid, params: data });
            this.saveLastJobUUID({ uuid: this.uuid, jobUUID });
          } catch (err) {
            /** Place what to do with error here */
            this.plotLoading = false;
            // eslint-disable-next-line no-console
            console.log(err);
            this.showDismissibleAlert = true;
          }
        }
      }
    },
    /**
     * Axios get call to get all the data from the API. This is called after
     * the axios post call once the jobUUID is stored.
     * @param {string} jobUUID - Data job UUID
     * @param {DateTime} startTime - The start time of the data request
     * @param {string|number|NodeJS.Timeout} interval - The js interval
     */
    async performGetData(jobUUID, startTime = null, interval = null) {
      const endTime = DateTime.utc();
      this.timeElapsed = Duration.fromMillis(endTime - startTime).toISOTime();

      try {
        // The actual axios get call. Passes in the jobUUID from the post call
        const response = await this.getDataRequest(jobUUID);
        // Saves the response from the get call
        const responseData = response.data;

        // This switch checks the response status to determine which steps need
        // to happen for the user
        switch (responseData.status) {
          /** Job completed */
          case 'completed':
            this.dataStatus = responseData.msg;
            clearInterval(interval);

            /** @todo Need to workout multiple traces */
            this.dataPointCount = responseData.result[0].count;
            if (!this.plotReady) {
              // eslint-disable-next-line no-console
              console.log(`Data request status: ${responseData.status}`);
              // eslint-disable-next-line no-console
              console.log(`Data request msg: ${responseData.msg}`);
              // Builds the plot based on the uuid, data, parameters, title,
              // request, and colorbar
              await this.renderPlot({
                chartUUID: this.uuid,
                data: responseData,
                parameterMapping: this.getParameters2(this.allParameters, true),
                title: this.plotTitle,
                requestBody: this.requestBody,
                colorscales,
              });
            }
            /**
             * Sets dataPointCount information in store
             * 9/14/2022 Don S: Currently not used, see TraceTabs.vue
             * line 162.
             */
            // this.setDataPointsCount({ dataPointsCount: this.dataPointCount });

            setTimeout(this.plotLoadingFalse, 2000); // close overlay after 2 seconds
            this.setHasDataFlag({ uuid: this.uuid, hasData: true });
            this.plotReady = true;
            break;
          /** Job failed */
          case 'failed':
            // eslint-disable-next-line no-console
            console.log(`Data request status: ${responseData.status}`);
            // eslint-disable-next-line no-console
            console.log(`Data request msg: ${responseData.msg}`);
            // TODO: Include a way to auto-generate issue to add to github
            // This is a user-friendly error message to display instead of msg from system
            this.dataStatus = 'Date request failed... Looks like something went wrong on our end. Please try again later.';
            this.setHasDataFlag({ uuid: this.uuid, hasData: false });
            clearInterval(interval); // clears the overlay
            setTimeout(this.plotLoadingFalse, 5000); // close overlay after 5 seconds
            break;
          /** Job exception */
          case 'job-exception':
            // eslint-disable-next-line no-console
            console.log(`Data request status: ${responseData.status}`);
            // eslint-disable-next-line no-console
            console.log(`Data request msg: ${responseData.msg}`);
            // TODO: Include a way to auto-generate issue to add to github
            // This is a user-friendly error message to display instead of msg from system
            this.dataStatus = 'Looks like something went wrong on our end. Please try again.';
            clearInterval(interval); // clears the overlay
            setTimeout(this.plotLoadingFalse, 5000); // close overlay after 5 seconds
            break;
          /**
           * Default when status is 'started' or 'pending'
           *
           * @todo Add check for data.request.msg that indicates the request is
           * stuck - "Something is wrong on our end. Please try again later."
           */
          default:
            // eslint-disable-next-line no-console
            console.log(`Data request status: ${responseData.status}`);
            // eslint-disable-next-line no-console
            console.log(`Data request msg: ${responseData.msg}`);
            this.dataStatus = responseData.msg;
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
      }
    },
    /**
     * A helped function to call performGetData which takes the jobUUID, a
     * start time for the data request. This function sets an interval
     * so the data status is checked every 5 seconds.
     * @param {string} jobUUID - Data job UUID
     * @param {DateTime} startTime - The start time of the data request
     */
    getDataInterval(jobUUID, startTime = null) {
      this.interval = setInterval(async () => {
        await this.performGetData(jobUUID, startTime, this.interval);
      }, 5000); // check data status every 5 seconds
    },
    /**
     * Parse parameter values
     * and make an object that split the data stream string
     * and parameter string.
     * @param {(string|object)} value - Parameter to be parsed
     * @param {boolean} isObject - Flag to indicate if value passed is object or not
     */
    parseParams(value, isObject = false) {
      let strList = _.split(value, '__');
      if (isObject) {
        strList = _.split(value.value, '__');
      }
      return { dataTable: strList[0], param: strList[1] };
    },
    /**
     * Reformats the selected axis object extracting
     * the data stream string.
     * @param {object} selectedAxis - The selected axis object
     */
    reformatSelectedAxis(selectedAxis) {
      return _.mapValues(selectedAxis, (value) => {
        if (_.isString(value)) {
          if (value !== 'time') {
            return this.parseParams(value);
          }
          return { dataTable: null, param: value };
        } if (_.isObject(value)) {
          // color axis
          if (value.active) {
            if (value !== 'time') {
              return this.parseParams(value, true);
            }
            return { dataTable: null, param: value };
          }
          return null;
        }
        return null;
      });
    },
    extractForm() {
      // const tzMap = {
      //   UTC: 'Z',
      // };
      // Getting request queries together
      const requestTemplate = {
        ref: '',
        x: '',
        y: '',
        start_dt: '',
        end_dt: '',
        z: '',
        color: '',
        download_format: '',
        download: false,
      };
      let axis = {};
      if (_.has(this.currentTrace, 'selectedAxis')) {
        axis = { ...this.currentTrace.selectedAxis };
      }
      // Parse and reformat selected axis to extract dataTables from param
      const reformatedAxis = this.reformatSelectedAxis(axis);
      const refs = [];
      // Extract dataTables from reformated axis and create a similar format as API request
      const axisResult = _.mapValues(reformatedAxis, (value) => {
        if (value) {
          if (value.dataTable) {
            refs.push(value.dataTable);
            return value.param;
          }
          return value.param;
        }
        return '';
      });

      let startDT; let
        endDT = null;
      if (_.has(this.currentTrace, 'dateTimeRange')) {
        startDT = this.startDateTime;
        endDT = this.endDateTime;
      }

      // Store axis result
      const request = {
        ...requestTemplate,
        ...axisResult,
        ref: refs.join(','),
        start_dt: startDT,
        end_dt: endDT,
      };
      this.requestBody = request;
      return _.uniq(refs.map((o) => o.split('-').slice(0, 2).join('-'))).length;
    },
    closeTab(id) {
      this.$emit('closeTab', id);
    },
    isDateTimeValid(dtStr, fmt) {
      const formats = {
        date: 'yyyy-MM-dd',
        time: 'HH:mm:ss',
      };
      const dtObj = DateTime.fromFormat(dtStr, formats[fmt]);
      if (dtObj.invalid === null) {
        return true;
      }
      return false;
    },
    setDTs(startDT, endDT) {
      this.startDate = startDT.toISODate();
      this.startTime = startDT.set({ hour: 0, minute: 0, seconds: 0 }).toFormat('HH:mm:ss');
      this.endDate = endDT.toISODate();
      this.endTime = endDT.set({ hour: 0, minute: 0, seconds: 0 }).toFormat('HH:mm:ss');
    },
    setOneDay() {
      // Gets the latest date with data and sets 1 day range
      const endDT = DateTime.fromISO(this.dtMax);
      const startDT = endDT.minus({ days: 1 });
      this.setDTs(startDT, endDT);
    },
    setOneWeek() {
      // Gets the latest date with data and sets 1 week range
      const endDT = DateTime.fromISO(this.dtMax);
      const startDT = endDT.minus({ days: 7 });
      this.setDTs(startDT, endDT);
    },
    setOneMonth() {
      // Gets the latest date with data and sets 1 month range
      const endDT = DateTime.fromISO(this.dtMax);
      const startDT = endDT.minus({ days: 30 });
      this.setDTs(startDT, endDT);
    },
    setSixMonths() {
      // Gets the latest date with data and sets 6 month range
      const endDT = DateTime.fromISO(this.dtMax);
      const startDT = endDT.minus({ months: 6 });
      this.setDTs(startDT, endDT);
    },
    setOneYear() {
      // Gets the latest date with data and sets 1 year range
      const endDT = DateTime.fromISO(this.dtMax);
      const startDT = endDT.minus({ years: 1 });
      this.setDTs(startDT, endDT);
    },
    clearStartDate() {
      this.dateTimeRange.start.date = '';
    },
    clearEndDate() {
      this.dateTimeRange.end.date = '';
    },
    // Gets the parameter name for tooltip hover
    getParamText(value) {
      if (value) {
        const flatParam = _.flatMap(this.parameterList.map((val) => (_.has(val, 'options') ? val.options : val)));
        const param = _.find(flatParam, ['value', value]);
        if (param) { // checks whether parameterList is empty
          return param.text;
        }
      }
      return 'Select a parameter';
    },
    getColorMin(value) {
      if (value) {
        return this.selectedAxis.color.range.min;
      }
      return 'Min';
    },
    getColorMax(value) {
      if (value) {
        return this.selectedAxis.color.range.max;
      }
      return 'Max';
    },
    getParameters2(allParameters, mapping = false) {
      const parameterValues = _.mapValues(
        allParameters,
        (value, key) => _.map(value, (o) => ({ instRef: key, ...o })),
      );
      const parameters = _.flatten(_.values(parameterValues));

      if (mapping) {
        return _.keyBy(_.uniqBy(
          parameters.map((param) => ({
            reference_designator: param.reference_designator,
            parameter_name: param.parameter_name,
            unit: param.unit,
            description: param.description,
          })),
          'parameter_name',
        ), 'reference_designator');
      }

      let timeObj;
      const paramArray = [];
      const groupedObj = {};
      parameters.forEach((param) => {
        if (param.reference_designator === 'time') {
          timeObj = { text: `${param.parameter_name}`, value: 'time' };
        } else {
          // Simplifying the parameter name so it is clearer to users
          // const streamType = _.startCase(_.lowerCase(_.split(`'${param.stream}'`, '-', 1)));

          // Checks if there is no stream code on the parameter listing
          if (!_.has(groupedObj, param.stream)) {
            groupedObj[param.stream] = [];
          }
          // Gets the text and value for the parameter listing
          groupedObj[param.stream].push({
            text: `${param.parameter_name}`,
            value: `${param.instRef}-${param.stream}__${param.reference_designator}`,
          });
        }
      });

      // Adds labels for each stream code
      _.forEach(groupedObj, (val, key) => {
        paramArray.push({
          label: key,
          options: _.sortBy(_.uniqBy(val, 'text'), 'text'),
        });
      });

      // Puts time at the start of the parameter list
      paramArray.unshift(timeObj);

      return paramArray;
    },
    /**
     * Get Data Availability from API
     * @param {array} selectedInstruments  - Array of selected instruments reference designator
     */
    async getDA(selectedInstruments) {
      if (!_.isEmpty(this.daAllAvailability)) {
        let checkedDA = {};
        /** If already requested, just filter down when inst is removed */
        if (selectedInstruments.length < _.keys(this.daAllAvailability).length) {
          checkedDA = _.pick(this.daAllAvailability, selectedInstruments);
        }
        if (!_.isEmpty(checkedDA)) {
          return { ...checkedDA };
        }
      }

      if (selectedInstruments.length > 0) {
        /** Filtered DA results, uses the selected instruments list */
        const DAPromises = selectedInstruments.map((inst) => axios.get(`${APIBASEURL}/${APIMETADATA}/${APIDA}`, {
          params: {
            ref: inst,
            average: false,
          },
        }));
        const responses = await axios.all(DAPromises);
        let daAvail = {};
        responses.forEach((response) => {
          daAvail = {
            ...daAvail,
            ...response.data,
          };
        });
        return daAvail;
      }
      return {};
    },
    /**
     * Parses dates into friendly format
     * @param {object} mergedDA - The data availability object to be transformed
     */
    transformMergedDA(mergedDA) {
      return _.mapKeys(mergedDA, (value, key) => DateTime.fromSeconds(parseInt(key)).toUTC().toFormat('yyyy-LL-dd'));
    },
    /**
     * Sets the calendar with data availability
     * based on selected axis values
     * @param {object} selectedAxis - The selected axis object containing params
     */
    setDA(selectedAxis) {
      const axisRefs = this.reformatSelectedAxis(selectedAxis);
      const allDA = this.daAllAvailability;
      if (!_.isEmpty(allDA)) {
        /** Loops over axis references to get unique streams data availability */
        const availabilities = {};
        _.forIn(axisRefs, (value) => {
          if (value && value.dataTable) {
            const tableKeys = value.dataTable.split('-');
            const instRD = tableKeys.slice(0, 4).join('-');
            const streamRD = tableKeys.slice(-2).join('-');
            if (!_.has(availabilities, streamRD)) {
              const data = allDA[instRD][streamRD];
              const count = _.keys(data).length;
              availabilities[streamRD] = {
                data,
                count,
              };
            }
          }
        });
        /** Remove zero availability */
        const filteredAvails = _.omitBy(availabilities, (o) => o.count === 0);
        /** Convert filtered to collection */
        const filteredCol = [];
        if (_.keys(filteredAvails).length > 0) {
          _.forEach(filteredAvails, (value, ref) => {
            filteredCol.push({
              ref,
              ...value,
            });
          });
        }
        /** Get the least count and transform to regular date string */
        const leastAvail = _.minBy(filteredCol, 'count');
        const finalDA = this.transformMergedDA(leastAvail.data);

        /** Sets the min and max dates on the calendar
         * so users can only select dates that have data */
        this.dtMin = _.head(_.keys(finalDA));
        this.dtMax = _.last(_.keys(finalDA));

        /** Fill calendar with data availaibility */
        this.dataAvailabilityCounts = finalDA;
      }
    },
    daAdder(objValue, srcValue) {
      return objValue + srcValue;
    },
    // Takes the counts on a date and sets the color
    searchDate(ymd) {
      if (_.has(this.dataAvailabilityCounts, ymd)) {
        const counts = this.dataAvailabilityCounts[ymd];
        if (counts > 0 && counts <= 5) {
          return ['da-midgreen', false];
        } if (counts === 0) {
          return ['da-zero', true];
        }
        return ['da-darkgreen', false];
      }
      return ['disabled', true];
    },
    dateDisabled(ymd) {
      return this.searchDate(ymd)[1];
    },
    dateClass(ymd) {
      return this.searchDate(ymd)[0];
    },
    getInstrumentDetails(refDes) {
      return axios
        .get(`${APIBASEURL}/${APIMETADATA}/instrument/${refDes}`);
    },
    async getAllParameters(instruments) {
      let allParameters = {};
      const results = await Promise.all(
        instruments.map(
          (refDes) => this.getInstrumentDetails(refDes),
        ),
      );
      // Extract each instrument products
      results.forEach((resp) => {
        const inst = resp.data;
        allParameters = {
          [inst.reference_designator]: inst.products,
          ...allParameters,
        };
      });
      return allParameters;
    },
    async fetchParameters(selectedInstruments) {
      this.allParameters = {};
      const results = await Promise.all(
        selectedInstruments.map(
          (refDes) => this.getInstrumentDetails(refDes),
        ),
      );
      // Extract each instrument products
      results.forEach((resp) => {
        const inst = resp.data;
        this.allParameters = {
          [inst.reference_designator]: inst.products,
          ...this.allParameters,
        };
      });
    },
    findInstrument(val) {
      return _.find(this.instruments, ['reference_designator', val]);
    },
    findSiteArea(val) {
      return _.find(this.sites, ['properties.reference_designator', val]);
    },
    // This is a datetime check that:
    // 1. Checks the start date is always before the end date
    // 2. Checks that the values inputed by the user are valid dates
    // 3. Checks that there is x, y, and colorbar value and date time are
    // valid
    dateTimeCheck() {
      // console.log(this.dateTimeRange.start.date);
      const startDT = DateTime.fromISO(
        `${this.dateTimeRange.start.date}T${this.dateTimeRange.start.time}`,
      );
      const endDT = DateTime.fromISO(
        `${this.dateTimeRange.end.date}T${this.dateTimeRange.end.time}`,
      );

      // start datetime is equal or after end datetime, invalid
      if (startDT >= endDT) {
        this.dateTimeInvalidState = true;
        this.requestReady = false;
      } else { // start datetime  is before end datetime, valid
        this.dateTimeInvalidState = false; // state to show alert is false
        // The state is valid, will check that an x, y, start&end date are filled in and enabled
        if (this.selectedAxis.x && this.selectedAxis.y
          && !this.startDateDisabled && !this.endDateDisabled) {
          this.requestReady = true;
        }
      }
    },
    /**
     * Check colorbar value is not null
     * or blank before plotting is allowed
     */
    colorBarCheck() {
      if (this.colorActive) {
        if (!this.colorValue) {
          this.colorAxisInvalidState = true;
          this.requestReady = false;
        } else {
          this.colorAxisInvalidState = false;
          this.requestReady = true;
        }
      } else {
        this.colorAxisInvalidState = false;
      }
    },
    removeDataset() {
      this.removeTrace({ id: this.currentTrace.id });
    },
    /**
     * Confirms trace form reset
     * Programmatically creates the confirmation modal
     */
    async confirmReset() {
      return this.$bvModal.msgBoxConfirm('Are you sure you want to reset the form? This will clear out all your current work.', {
        title: 'Confirm form and plot reset',
        size: 'lg',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'YES, RESET FORM.',
        cancelVariant: 'primary',
        cancelTitle: 'NO, DO NOT RESET FORM.',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true,
      });
    },
    async resetForm() {
      try {
        const value = await this.confirmReset();
        if (value) {
          this.handleOk();
        }
      } catch (err) {
        /** Place what to do with error here */
      }
    },
    /**
     * Handle reset form okay button
     * - Remove the all the selected instruments
     * - Reset and clear out the form and chart
     */
    handleOk() {
      this.resetTrace({ id: this.id });
      this.resetPlotLayout({
        uuid: this.uuid,
      });
      this.setHasDataFlag({ uuid: this.uuid, hasData: false });
      this.allParameters = {};

      /** Clear out url queries that has to do with params */
      const query = {
        ...this.$route.query,
      };
      const finalQuery = _.omitBy(query, (value, key) => {
        if (['x', 'y'].includes(key) || key.includes('color') || key.includes('dt')) {
          return value;
        }
        return null;
      });

      /** Removes all selected Instruments */
      _.forEach(this.selectedInstruments, (ref) => {
        this.removeInstrument({ id: this.id, ref });
      });

      /** Reset url path */
      this.$router.push({ query: finalQuery });
    },
  },
  computed: {
    ...mapState({
      sites: (state) => state.sites.features,
      instruments: (state) => state.instruments,
      worker: (state) => state.worker,
      researchSites: (state) => state.areas.features,
      charts: (state) => state.charts,
      globalRanges: (state) => state.globalRanges,
    }),
    ...mapGetters('visualization/charts', {
      getChart: 'find',
      charts: 'list',
      chartsLength: 'count',
    }),
    ...mapGetters('visualization/traces', {
      getTraces: 'findByUUID',
      getTrace: 'find',
      getInstruments: 'findSelectedInstruments',
      getSelectedAxis: 'getSelectedAxis',
      getDateTimeRange: 'getDateTimeRange',
    }),
    ...mapState('visualization/traces', {
      allTraces: (state) => state.byId,
    }),
    uuid() {
      return this.chartUUID;
    },
    chart() {
      if (this.chartsLength === 0) {
        return null;
      }
      return this.getChart(this.uuid);
    },
    traces() {
      return this.getTraces(this.uuid);
    },
    currentTrace() {
      if (this.traces.length > 0) {
        return this.allTraces[this.$route.query.d];
      }
      return null;
    },
    id() {
      if (this.currentTrace) {
        return this.currentTrace.id;
      }
      return 1;
    },
    // === Form input states ===
    selectedAxis() {
      return this.getSelectedAxis(this.currentTrace ? this.currentTrace.id : -1);
    },
    dateTimeRange() {
      return this.getDateTimeRange(this.currentTrace ? this.currentTrace.id : -1);
    },
    paramInstruments() {
      const instruments = [];
      const parameters = ['x', 'y', 'color'].map((k) => this.selectedParameters[k]);
      parameters.forEach((p) => {
        if (!_.isNull(p) && p !== 'time') {
          const ref = p.split('-').slice(0, 4).join('-');
          instruments.push(ref);
        }
      });
      return _.uniq(instruments);
    },
    selectedParameters() {
      const params = {
        x: this.xAxis,
        y: this.yAxis,
        color_active: this.colorActive,
        color: this.colorValue,
        color_min: this.colorMin,
        color_max: this.colorMax,
        color_theme: this.colorTheme,
      };
      return params;
    },
    xAxis: {
      get() {
        if (!_.has(this.$route.query, 'x')) {
          return this.selectedAxis.x;
        }
        return this.$route.query.x;
      },
      set(value) {
        this.updateX({ id: this.id, value });
        if (value) {
          this.routeSelections('x', value);
        }
      },
    },
    yAxis: {
      get() {
        if (!_.has(this.$route.query, 'y')) {
          return this.selectedAxis.y;
        }
        return this.$route.query.y;
      },
      set(value) {
        this.updateY({ id: this.id, value });
        if (value) {
          this.routeSelections('y', value);
        }
      },
    },
    colorActive: {
      get() {
        if (!_.has(this.$route.query, 'color_active')) {
          return this.selectedAxis.color.active;
        }
        return JSON.parse(this.$route.query.color_active);
      },
      set(value) {
        this.updateColor({
          id: this.id,
          value: {
            ...this.selectedAxis.color,
            active: value,
          },
        });
        if (value !== null) {
          this.routeSelections('color_active', value);
        }
      },
    },
    colorValue: {
      get() {
        if (!_.has(this.$route.query, 'color')) {
          return this.selectedAxis.color.value;
        }
        return this.$route.query.color;
      },
      set(value) {
        this.updateColor({
          id: this.id,
          value: {
            ...this.selectedAxis.color,
            value,
          },
        });
        if (value) {
          this.routeSelections('color', value);
        }
      },
    },
    colorMin: {
      get() {
        if (!_.has(this.$route.query, 'color_min')) {
          return this.selectedAxis.color.range.min;
        }
        return this.$route.query.color_min;
      },
      set(value) {
        this.updateColor({
          id: this.id,
          value: {
            ...this.selectedAxis.color,
            range: {
              ...this.selectedAxis.color.range,
              min: value,
            },
          },
        });
        if (value) {
          this.routeSelections('color_min', value);
        }
      },
    },
    colorMax: {
      get() {
        if (!_.has(this.$route.query, 'color_max')) {
          return this.selectedAxis.color.range.max;
        }
        return this.$route.query.color_max;
      },
      set(value) {
        this.updateColor({
          id: this.id,
          value: {
            ...this.selectedAxis.color,
            range: {
              ...this.selectedAxis.color.range,
              max: value,
            },
          },
        });
        if (value) {
          this.routeSelections('color_max', value);
        }
      },
    },
    colorTheme: {
      get() {
        if (!_.has(this.$route.query, 'color_theme')) {
          return this.selectedAxis.color.colorbar;
        }
        return this.$route.query.color_theme;
      },
      set(value) {
        this.updateColor({
          id: this.id,
          value: {
            ...this.selectedAxis.color,
            colorbar: value,
          },
        });
        if (value) {
          this.routeSelections('color_theme', value);
        }
      },
    },
    timeZone: {
      get() {
        return 'Z';
      },
      set(value) {
        // eslint-disable-next-line no-console
        console.log(`Timezone: ${value}`);
      },
    },
    startDT() {
      if (!_.has(this.$route.query, 'start_dt')) {
        return null;
      }
      return this.$route.query.start_dt.match(this.dateTimeRegex);
    },
    endDT() {
      if (!_.has(this.$route.query, 'end_dt')) {
        return null;
      }
      return this.$route.query.end_dt.match(this.dateTimeRegex);
    },
    startDate: {
      get() {
        if (this.startDT === null || this.startDT.length !== 3) {
          return this.dateTimeRange.start.date;
        }
        // get date from startDT if not null
        return this.startDT[1];
      },
      set(value) {
        this.updateStartDate({ id: this.id, value });
      },
    },
    startDateValid() {
      if (this.startDate) {
        return this.isDateTimeValid(this.startDate, 'date');
      }
      return null;
    },
    startTime: {
      get() {
        if (this.startDT === null || this.startDT.length !== 3) {
          return this.dateTimeRange.start.time;
        }
        // get time from startDT if not null
        return this.startDT[2];
      },
      set(value) {
        this.updateStartTime({ id: this.id, value });
      },
    },
    startTimeValid() {
      if (this.startTime) {
        return this.isDateTimeValid(this.startTime, 'time');
      }
      return null;
    },
    endDate: {
      get() {
        if (this.endDT === null || this.endDT.length !== 3) {
          return this.dateTimeRange.end.date;
        }
        // get date from startDT if not null
        return this.endDT[1];
      },
      set(value) {
        this.updateEndDate({ id: this.id, value });
      },
    },
    endDateValid() {
      if (this.endDate) {
        return this.isDateTimeValid(this.endDate, 'date');
      }
      return null;
    },
    endTime: {
      get() {
        if (this.endDT === null || this.endDT.length !== 3) {
          return this.dateTimeRange.end.time;
        }
        // get time from startDT if not null
        return this.endDT[2];
      },
      set(value) {
        this.updateEndTime({ id: this.id, value });
      },
    },
    endTimeValid() {
      if (this.endTime) {
        return this.isDateTimeValid(this.endTime, 'time');
      }
      return null;
    },
    startDateTime() {
      return `${this.startDate}T${this.startTime}${this.timeZone}`;
    },
    endDateTime() {
      return `${this.endDate}T${this.endTime}${this.timeZone}`;
    },
    // === Form input state end ===
    plotLoading: {
      get() {
        return this.chart.plotLoading;
      },
      set(value) {
        this.updateChartStatus({
          uuid: this.uuid,
          chartStatus: 'plotLoading',
          value,
        });
      },
    },
    timeElapsed: {
      get() {
        return this.chart.timeElapsed;
      },
      set(value) {
        this.updateChartStatus({
          uuid: this.uuid,
          chartStatus: 'timeElapsed',
          value,
        });
      },
    },
    dataStatus: {
      get() {
        return this.chart.dataStatus;
      },
      set(value) {
        this.updateChartStatus({
          uuid: this.uuid,
          chartStatus: 'dataStatus',
          value,
        });
      },
    },
    filteredAreas() {
      return _.filter(
        this.researchSites,
        (o) => o.properties.reference_designator !== 'mid-plate',
      );
    },
    barClass() {
      return `h-100 colorbar-colors ${this.colorTheme}`;
    },
    colorbarChoices() {
      const colorbarList = _.keys(colorscales);
      return colorbarList.map((val) => ({
        text: val,
        value: val,
      }));
    },
    traceInstruments() {
      return this.getInstruments(this.id);
    },
    selectedInstruments() {
      return this.traceInstruments.map((o) => o.ref);
    },
    plotTitle() {
      if (this.selectedInstruments.length > 0) {
        const instObj = this.selectedInstruments.map(
          (val) => this.findInstrument(val),
        );
        const instGroupCode = instObj.map((val) => val.group_code);
        const instArea = instObj.map(
          (val) => this.findSiteArea(val.site_rd),
        )[0].properties.site_area;
        return `${instGroupCode} at ${instArea}`;
      }
      return '';
    },
    startDateDisabled() {
      if (this.dateTimeRange.start.date !== '') {
        return this.dateDisabled(this.dateTimeRange.start.date);
      }
      return false;
    },
    endDateDisabled() {
      if (this.dateTimeRange.end.date !== '') {
        return this.dateDisabled(this.dateTimeRange.end.date);
      }
      return false;
    },
    emptyParams() {
      return _.isEmpty(this.allParameters);
    },
    noParamsSelected() {
      // Both x and y have to have parameters selected in order for date/time to be enabled
      if (!_.isEmpty(this.xAxis) && !_.isEmpty(this.yAxis) && !this.emptyParams) {
        return false;
      }
      return true;
    },
  },
  watch: {
    allParameters(value) {
      this.parameterList = this.getParameters2(value);
    },
    async selectedInstruments(newVal) {
      if (newVal.length === 0) {
        this.localSelectedInst = [...newVal];
        // this.setHasDataFlag({ uuid: this.uuid, hasData: false });
        this.allParameters = {};
      } else if (!_.isEqual(this.localSelectedInst, newVal)) {
        // Get parameters from API each time selected instruments changes
        this.allParameters = await this.getAllParameters(newVal);
        /** anytime new instrument is selected, get data availability */
        this.daAllAvailability = await this.getDA(newVal);
        this.localSelectedInst = [...newVal];
      }
    },
    selectedAxis: {
      async handler(value) {
        this.setDA(value);
        this.dateTimeCheck();
        this.colorBarCheck();
        this.extractForm();
      },
      deep: true,
    },
    requestBody(newVal) {
      if (!_.isEmpty(newVal)) {
        this.validRequest = this.validateRequest(newVal);
      } else {
        this.validRequest = false;
      }
    },
    dateTimeRange: {
      handler(value) {
        this.dateTimeCheck();
        this.colorBarCheck();
        this.extractForm();
        if (value.start.date !== '' && value.start.time !== '') {
          this.routeSelections('start_dt', this.createDTString(value.start.date, value.start.time));
        }

        if (value.end.date !== '' && value.end.time !== '') {
          this.routeSelections('end_dt', this.createDTString(value.end.date, value.end.time));
        }
      },
      deep: true,
    },
  },
};
</script>

<style>
/* Styling for add colorbar text */
#color-add-text {
  font-weight: bold;
}
/* Styling used for calendar on form inputs */
.da-zero {
  background-color: #c4c4c4;
}
/* Controls box color option for data availability*/
.da-midgreen {
  background-color: #9cc069;
}
.da-darkgreen {
  background-color: #3b6428;
}

/* Controls int color on calendar  */
.da-midgreen > .text-dark {
  color: #f2f2f2 !important;
}
/* Controls int color on calendar */
.da-midgreen > .text-dark:hover {
  color: #323232 !important;
}
/* Controls int color on calendar */
.da-darkgreen > .text-dark {
  color: #f2f2f2 !important;
}
/* Controls int color on calendar */
.da-darkgreen > .text-dark:hover {
  color: #323232 !important;
}
/*---------------------------------*/
/* Styling used for colorbar theme options */
.colorbar-colors {
  min-width: 50px !important;
  height: calc(1.5em + 0.75rem + 2px);
}
.thermal {
  background: linear-gradient(
    to right,
    rgb(3.9667833995429036, 35.252282593483415, 51.46177604623678),
    rgb(18.873564708433232, 50.64156523304523, 113.53422502157348),
    rgb(73.20221438784256, 54.179630353385264, 159.28371024356304),
    rgb(115.65786637302985, 73.06091200273256, 146.41500300978396),
    rgb(154.32387640747461, 88.36149134908872, 136.9689351820826),
    rgb(197.63085871786257, 101.72828810672026, 119.1742487844456),
    rgb(234.5933859542238, 120.5075625653944, 87.63747737529172),
    rgb(251.14008799497, 157.91878161860828, 61.667170030613086),
    rgb(248.0853098343754, 203.0387801120806, 67.17368057344709),
    rgb(231.80566962518793, 250.450138612026, 90.65449063963804)
  );
  background-repeat: no-repeat;
}
.haline {
  background: linear-gradient(
    to right,
    rgb(41.553003412010725, 24.28005873490703, 107.7560958148976),
    rgb(37.138903238485575, 44.50497946642615, 162.8605330109369),
    rgb(12.208240212608644, 83.18051173360976, 147.68391004421872),
    rgb(31.977483065522705, 109.95567121162365, 138.67189999704667),
    rgb(51.759424692205506, 133.93331627058967, 136.28080181863936),
    rgb(67.2819882455002, 159.84853963503437, 132.47598166990159),
    rgb(89.50798359362147, 185.27474433334945, 120.06315538995962),
    rgb(136.67934688087553, 208.28483800371586, 96.74971650780321),
    rgb(202.0563209805993, 222.71410596129633, 104.85153437439057),
    rgb(253.49054803003938, 238.8655333884997, 153.68084905623283)
  );
  background-repeat: no-repeat;
}
.solar {
  background: linear-gradient(
    to right,
    rgb(51.36340044476597, 19.713485061199627, 23.822161264409026),
    rgb(86.14297056026972, 30.569390693424058, 34.55925998661305),
    rgb(120.9051252487905, 40.66659022656687, 35.8551735019984),
    rgb(151.04874375697864, 59.24619363724781, 27.916437809439422),
    rgb(172.56352465535286, 85.14919499785412, 20.983493127349497),
    rgb(190.0427308219606, 115.16304028591053, 19.199757402744464),
    rgb(203.47150344891767, 146.13475714221298, 25.60747301349278),
    rgb(214.1938509427738, 180.2488372420938, 39.266946070094924),
    rgb(221.35886593490193, 215.45913064502892, 56.124110800750245),
    rgb(224.52954149176304, 253.46697030423755, 74.88008823338993)
  );
  background-repeat: no-repeat;
}
.ice {
  background: linear-gradient(
    to right,
    rgb(3.904476960636511, 5.742751441183806, 18.5458280265648),
    rgb(29.742069195308062, 29.081433769364825, 59.13032612379715),
    rgb(51.43019215575712, 50.346440898129245, 103.42747602971113),
    rgb(62.6637435392901, 75.47695483907094, 150.07511415921164),
    rgb(62.43183803027079, 106.20185148578827, 176.48884154421538),
    rgb(74.01453672693648, 137.9653102064432, 188.74763844617382),
    rgb(97.45050135019153, 167.67216364131534, 199.40565656814152),
    rgb(132.51515305391268, 198.00972759239932, 211.46079924536747),
    rgb(182.68154943905805, 224.78051389904206, 228.18648647256563),
    rgb(234.105120482072, 252.9124516503815, 253.17238027702751)
  );
  background-repeat: no-repeat;
}
.gray {
  background: linear-gradient(
    to right,
    rgb(0.00013053740289167238, 0.0005233288998937221, 0.0015255573749666492),
    rgb(21.67192382269842, 21.3399475675765, 21.275560698000003),
    rgb(48.03625202591796, 47.578320679994874, 47.39592787782459),
    rgb(74.15293525035652, 73.64965833996182, 73.3339446450999),
    rgb(99.58492079324715, 99.08694033678762, 98.63109376558887),
    rgb(126.91980463079491, 126.46091826223106, 125.84728289743131),
    rgb(154.84817226424454, 154.44849024079832, 153.6691231958653),
    rgb(185.95170358556422, 185.62615733923622, 184.65960393983144),
    rgb(218.70498232967753, 218.45484347563848, 217.29043711205765),
    rgb(254.86132988058, 254.68134219584223, 253.2995124948098)
  );
  background-repeat: no-repeat;
}
.oxy {
  background: linear-gradient(
    to right,
    rgb(63.83205110993896, 5.2179051169453095, 5.0155733864456735),
    rgb(111.32004242653103, 5.841714242012307, 15.039668320441022),
    rgb(84.12184431862899, 83.6159948370921, 83.24632836662654),
    rgb(111.32407968833061, 110.83967630062348, 110.31677759973934),
    rgb(139.04447272646766, 138.60970836936644, 137.92453100777908),
    rgb(170.37022951393755, 170.00723021456022, 169.13465006028673),
    rgb(202.55492677766105, 202.26855332746777, 201.20168578285882),
    rgb(239.00788678127793, 238.79931636878882, 237.51259989085986),
    rgb(232.06917726555062, 220.00541348417778, 47.54943795870078),
    rgb(220.54668278037454, 174.92490197406946, 25.497038211532495)
  );
  background-repeat: no-repeat;
}
.deep {
  background: linear-gradient(
    to right,
    rgb(39.806003535935474, 26.165104409830633, 44.044000253923855),
    rgb(57.1664432237624, 46.808478140587226, 85.25750421350236),
    rgb(65.4343171186481, 69.00322669810848, 130.76307178326152),
    rgb(61.72638904987155, 99.85410287888797, 148.67654098306417),
    rgb(67.53724752325135, 128.01233134824838, 154.53107779964586),
    rgb(77.33638716275782, 156.9863458093556, 160.94703486180308),
    rgb(92.86831005836805, 185.56219978868532, 163.9537104022011),
    rgb(135.18132032821643, 212.29738331139853, 163.07612577336644),
    rgb(195.61297820889592, 232.86815622205665, 175.2889467196488),
    rgb(253.17348001728232, 253.56523110185535, 204.0347298651208)
  );
  background-repeat: no-repeat;
}
.dense {
  background: linear-gradient(
    to right,
    rgb(54.31090526102163, 14.252380046533654, 36.26342424013871),
    rgb(85.7989759802251, 22.06461261632537, 74.71224699302037),
    rgb(106.78660038324533, 39.527301944204844, 120.70452517708499),
    rgb(118.3516858026774, 66.46909065613033, 164.53566444075537),
    rgb(121.08270116842593, 96.73382662631964, 199.62555204425132),
    rgb(116.69179201518475, 131.0574687077482, 223.00246562883132),
    rgb(118.251357233103, 163.61740904027724, 228.53161034912037),
    rgb(143.18922717921836, 192.826268066118, 226.27953365660565),
    rgb(183.1351101917053, 217.60307997847204, 228.251696238598),
    rgb(230.06155183616042, 240.76584843683253, 240.66969638285332)
  );
  background-repeat: no-repeat;
}
.algae {
  background: linear-gradient(
    to right,
    rgb(17.55839067964479, 36.23281232955577, 20.153575203990844),
    rgb(24.637110317121774, 60.21077433538822, 38.839213813153435),
    rgb(25.373699104576946, 84.37542479416294, 55.08417304581284),
    rgb(17.57585421417838, 110.06839355576057, 68.68399952446144),
    rgb(6.858404623297841, 135.33528066459687, 77.4758059069887),
    rgb(43.71543297763709, 160.37315745497008, 81.80409909493339),
    rgb(100.03072435519584, 180.30072401548756, 98.87705874058494),
    rgb(143.14860909312404, 201.60342523575196, 131.32679811778968),
    rgb(179.7422477652839, 224.1757042885966, 167.77906099156073),
    rgb(214.94007726060866, 249.11277529871208, 207.73564072420987)
  );
  background-repeat: no-repeat;
}
.matter {
  background: linear-gradient(
    to right,
    rgb(47.21878677301089, 15.079039274757633, 61.97588197756816),
    rgb(83.3363402227638, 22.38457886644779, 84.09775748542634),
    rgb(120.99300654017593, 26.42178582117039, 97.35036482484378),
    rgb(158.84876795898214, 35.84349978858338, 98.45173065412862),
    rgb(191.82808630490047, 54.43149229532446, 91.06982430498441),
    rgb(218.95492047994702, 83.85484170029459, 82.91141058334337),
    rgb(234.7135564906137, 120.34714348232282, 88.2587138340288),
    rgb(244.06404077570824, 159.8677677283578, 109.90212753450986),
    rgb(249.71873574367936, 198.23140465727673, 139.9323685206129),
    rgb(253.54487181508566, 237.23358780741805, 176.22971007370938)
  );
  background-repeat: no-repeat;
}
.turbid {
  background: linear-gradient(
    to right,
    rgb(34.167993781172655, 30.526401043097934, 27.07913045813171),
    rgb(62.22904977078704, 48.424231940087736, 39.83677243701294),
    rgb(90.9839496856069, 65.11254931182745, 49.68400674367969),
    rgb(121.07926504142, 82.51771365514685, 55.973191075023216),
    rgb(148.59853329950755, 101.30165420068133, 58.32610254127212),
    rgb(173.0396540192162, 124.59457984482917, 60.910115253495334),
    rgb(191.1578384532829, 151.3253742294932, 71.10566865100417),
    rgb(205.17620337138018, 182.22663842399953, 96.18553688027787),
    rgb(217.97649093647135, 213.470277667311, 130.96838668231337),
    rgb(232.77031959624443, 245.79586371708592, 171.4489667987578)
  );
  background-repeat: no-repeat;
}
.speed {
  background: linear-gradient(
    to right,
    rgb(23.085854779154044, 35.021344934017755, 18.680691646361467),
    rgb(24.327812345704086, 61.565833231262836, 36.83896554113322),
    rgb(13.647865954773865, 88.27783168212805, 44.635699364795855),
    rgb(24.24752247273632, 114.52158266179418, 39.867559416789426),
    rgb(69.76026165623944, 136.30261432003994, 23.12573710584369),
    rgb(122.29360974357972, 155.0230816482595, 5.560439778611055),
    rgb(170.28102474708956, 171.94665898585257, 32.432573938237006),
    rgb(211.00982311640985, 192.61951487549229, 84.71308443305507),
    rgb(235.87247670955992, 220.13200282327384, 145.20086734070523),
    rgb(254.90445642601293, 252.79963626326676, 205.04581718375292)
  );
  background-repeat: no-repeat;
}
.amp {
  background: linear-gradient(
    to right,
    rgb(60.194372989476584, 9.000857386240273, 17.706547810502023),
    rgb(96.11790737208179, 14.340327327215716, 33.97432696933816),
    rgb(133.37827928627576, 14.242728545916016, 41.36940848895977),
    rgb(166.09935594519263, 34.4368747880717, 36.54482622104953),
    rgb(185.15698654301144, 70.41170596403225, 44.944897554171085),
    rgb(197.71359198343777, 106.19232177212118, 77.03135774922866),
    rgb(207.90988301756028, 138.50396812148622, 114.80370292553326),
    rgb(217.8181134232075, 171.5076480324349, 156.17450876506544),
    rgb(228.0179679624237, 203.8309705193407, 196.67085630151948),
    rgb(241.31850831785724, 236.8975842696571, 236.0670766338123)
  );
  background-repeat: no-repeat;
}
.tempo {
  background: linear-gradient(
    to right,
    rgb(20.975177818185685, 29.305724033046026, 67.52149278392186),
    rgb(26.534184307241947, 56.33616755394477, 83.22871183111204),
    rgb(27.834702708870225, 81.98048071693896, 99.47344318725588),
    rgb(20.837055319573878, 109.02936568602325, 114.54753246500141),
    rgb(22.478531273315777, 135.23815766321275, 124.46083684797733),
    rgb(71.47659552521036, 159.2841628666741, 130.47987200069983),
    rgb(125.40240240038942, 178.7341879813931, 143.75398616807968),
    rgb(174.00841013421186, 198.87480296458568, 169.6637540457679),
    rgb(215.78864578448477, 220.56670133315032, 203.92028260750473),
    rgb(254.63696406869227, 245.64061813707673, 244.23632342897335)
  );
  background-repeat: no-repeat;
}
.rain {
  background: linear-gradient(
    to right,
    rgb(33.550899829470936, 26.521417561333926, 56.44077356903427),
    rgb(37.17686118077636, 53.57004119279176, 77.27258614360846),
    rgb(26.38402964212206, 80.16729629985736, 98.50727263891169),
    rgb(3.5168645182826364, 107.45345621724091, 108.7558614552492),
    rgb(30.320571604469222, 131.91934557912862, 110.02678896904061),
    rgb(93.42933124288716, 151.22064324700736, 112.2177241224252),
    rgb(145.28835789813584, 167.08279573916332, 125.32956370194162),
    rgb(194.8387671765173, 183.38693071777502, 145.29307047636627),
    rgb(220.41552366135048, 207.29766503844803, 195.350752173558),
    rgb(238.32043006102657, 237.36594765274904, 242.94160075497095)
  );
  background-repeat: no-repeat;
}
.phase {
  background: linear-gradient(
    to right,
    rgb(167.86864018752402, 119.83448810303591, 12.60028492016953),
    rgb(202.8540440890336, 90.45612396900981, 59.83409913640899),
    rgb(221.9450490691268, 52.70336959140187, 123.20583928759594),
    rgb(207.87771952608514, 47.25145956735892, 208.4169581184993),
    rgb(160.30392381878647, 93.24306999081533, 243.5750014477597),
    rgb(87.62763726216158, 130.8664027951857, 223.41229220368172),
    rgb(30.04066871274612, 147.36764983742205, 167.72174313358624),
    rgb(15.127131777533364, 153.60763634595807, 107.20338530325428),
    rgb(104.24891254730414, 145.2881037965907, 25.131579066651234),
    rgb(167.86864018749915, 119.83448810304589, 12.60028492046341)
  );
  background-repeat: no-repeat;
}
.topo {
  background: linear-gradient(
    to right,
    rgb(39.806003535935474, 26.165104409830633, 44.044000253923855),
    rgb(65.4343171186481, 69.00322669810848, 130.76307178326152),
    rgb(67.2348251768625, 127.02359386643434, 154.30893940111628),
    rgb(92.86831005836805, 185.56219978868532, 163.9537104022011),
    rgb(193.48037229681188, 232.15601867502897, 174.51691907609046),
    rgb(25.838807657002214, 61.94800379950847, 27.968805823105935),
    rgb(86.38473137419588, 101.94864334279394, 52.9710890735624),
    rgb(164.28355371082122, 138.25515576636386, 63.4539532343566),
    rgb(213.24462348832242, 188.78460482719112, 128.77815179693087),
    rgb(248.57125042784426, 253.30474465633887, 228.20532503080892)
  );
  background-repeat: no-repeat;
}
.balance {
  background: linear-gradient(
    to right,
    rgb(60.194372989476584, 9.000857386240273, 17.706547810502023),
    rgb(133.521059860059, 14.257292579904288, 41.367764067463646),
    rgb(184.76274378656808, 69.39272944187744, 44.314674975191764),
    rgb(208.0253783484113, 138.88350633206133, 115.27063881892165),
    rgb(227.79866511715926, 203.18027859804522, 195.86806623855207),
    rgb(202.09046347568597, 211.67796465013467, 215.79300939620725),
    rgb(116.6168786913811, 169.5488339048715, 189.5755492095059),
    rgb(35.201019102092495, 121.60151326889931, 186.4890263058305),
    rgb(40.523725432054675, 65.94356329960742, 161.7511901844389),
    rgb(23.759956959295252, 28.349199016788695, 66.68565908102896)
  );
  background-repeat: no-repeat;
}
.delta {
  background: linear-gradient(
    to right,
    rgb(23.085854779154044, 35.021344934017755, 18.680691646361467),
    rgb(13.600071021140932, 88.38121098545308, 44.642270979443765),
    rgb(68.3561081740958, 135.7530243404567, 23.713407300877762),
    rgb(170.81864083630103, 172.1548670996092, 32.928979109136165),
    rgb(235.46875300641685, 219.5232956689137, 143.99336781491192),
    rgb(208.56213865469823, 224.36740673230415, 213.76382057849634),
    rgb(108.74870840686218, 181.56197500492246, 179.15079855093876),
    rgb(40.45090976519951, 129.63441534903117, 165.50114888808653),
    rgb(31.98827234528727, 72.78331478623741, 154.58611004799786),
    rgb(16.824233433518643, 31.584312734240594, 63.61769579267813)
  );
  background-repeat: no-repeat;
}
.curl {
  background: linear-gradient(
    to right,
    rgb(20.975177818185685, 29.305724033046026, 67.52149278392186),
    rgb(27.82530906500158, 82.08155573417052, 99.5355457957003),
    rgb(21.749639385149376, 134.52711532084146, 124.26741099452158),
    rgb(126.00066025041343, 178.95949215356308, 143.9861068151915),
    rgb(214.99269161345055, 220.10548852663592, 203.1728717071964),
    rgb(238.63643362765615, 210.79490987429654, 196.96240238319294),
    rgb(219.12469349785098, 140.7845925802209, 119.31312127179554),
    rgb(183.24532799263645, 73.56626378094228, 95.95957498761506),
    rgb(124.28381626003002, 27.868972829918953, 94.67988922952024),
    rgb(51.86706281603707, 13.070573977206095, 53.17698762616727)
  );
  background-repeat: no-repeat;
}
.diff {
  background: linear-gradient(
    to right,
    rgb(28.157358940674747, 34.14875008810586, 6.89504620731721),
    rgb(80.7743624318833, 75.12904472555562, 30.119952997241974),
    rgb(129.9161560087964, 116.7682042447814, 75.9934239456374),
    rgb(176.00547554790538, 166.45446858595537, 142.01580274303743),
    rgb(226.77400282229175, 220.72172384486478, 212.44127809044988),
    rgb(223.64013271818828, 223.6472915281929, 225.73208575307703),
    rgb(160.49920685251072, 171.40503971993715, 180.36215062286263),
    rgb(99.86314747643274, 124.16451576806283, 141.22982698515193),
    rgb(40.25199796691063, 81.86577420643941, 109.170531126018),
    rgb(7.85442132403282, 34.896419521677664, 63.71084336277833)
  );
  background-repeat: no-repeat;
}
.tarn {
  background: linear-gradient(
    to right,
    rgb(22.904929948713956, 35.365547163445875, 13.616068606232362),
    rgb(68.43144265394346, 80.25581463814989, 16.01951974518775),
    rgb(145.90816188763523, 110.82524280251539, 41.180140297795944),
    rgb(211.3489975052571, 151.85873716359907, 103.67131412648514),
    rgb(238.50935644044657, 217.08853796484487, 201.48432954748853),
    rgb(231.0567675690014, 223.46341380804148, 201.58109863822273),
    rgb(150.50068578705492, 177.56785124965947, 152.25103534006794),
    rgb(61.347944531984204, 134.13214773723672, 128.4679512799909),
    rgb(23.502605469940665, 84.35399837916037, 108.17675484925958),
    rgb(15.688279723124817, 30.158096721367595, 79.27424531458526)
  );
  background-repeat: no-repeat;
}
</style>
